import { useNosIdMutation } from '@/hooks/api/base.hooks';
import { api, type Profile, type SecurityCode } from '@/portal-api.definition';
import { ActionType, useI18n, useToast } from '@nosinovacao/nosid-mfe-common';
import { profilesQueryOptions } from '@/hooks/api';
import { useQueryClient } from '@tanstack/react-query';
import { type ProfileForm } from '../../../-model/profile-form';

export function useUpdateProfile() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  return useNosIdMutation({
    mutationFn: ({
      profile: oldProfile,
      sa,
      newValues,
      profilePin,
      securityCode,
    }: {
      newValues: ProfileForm;
      profile: Profile;
      sa: string;
      profilePin?: string;
      securityCode?: SecurityCode;
    }) => {
      const profilePayload: Profile = {
        ...oldProfile,
        ServiceAccount: sa,
        Type: newValues.type,
        Avatar: newValues.avatar ?? undefined,
        ContentRatingLimit: newValues.type === 'Kids' ? (newValues.ageRating ?? '') : 'Todos',
        Nickname: newValues.name,
        DisplayName: newValues.name,
        Pin:
          newValues.hasPin && newValues.type !== 'Kids' ? (newValues.pin ?? undefined) : undefined,
      };

      if (profilePin || securityCode) {
        return api.put('api/v2/profiles', {
          Profile: profilePayload,
          ProfilePin: profilePin,
          SecurityCode: securityCode,
        });
      }

      return api.put('api/v1/profiles', profilePayload);
    },
    errorLogAction: () => ({ ActionType: ActionType.Submit }),
    successLogAction: () => ({ ActionType: ActionType.Submit }),
    onSuccess: (newProfile) => {
      queryClient.setQueryData(
        profilesQueryOptions(newProfile.ServiceAccount ?? '').queryKey,
        (old) => old?.map((p) => (p.ProfileId === newProfile.ProfileId ? newProfile : p)) ?? [],
      );
      toast.show('success', t('Common.SnackBar.ProfileUpdated'));
    },
  });
}
